import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    MobileStepper,
    Radio,
    RadioGroup,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from "@mui/material";
import { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import React from "react";
import CheckoutCart from "../../components/checkout/CheckoutCart";
import PageWrapper from "../../components/PageWrapper";
import config from "../../config";
import {
    CANADA_ID,
    CountryOptions,
    ProvinceOptions,
    StateOptions,
    US_ID,
} from "../../models/AddressFormSelections";
import { ClientConfiguration } from "../../models/ClientConfiguration";
import { EcommerceEventList } from "../../models/Event";
import {
    AddressType,
    Order,
    OrderStatus,
    PaymentMethod,
    TransactionLogStatus,
} from "../../models/Order";
import {
    EcommercePayment,
    OfflinePayment,
    OrderAddress,
    OtherAddress,
    SquareOnlinePayment,
} from "../../requests/OrderRequests";
import AuthStore from "../../stores/AuthStore";
import { useCartStore } from "../../stores/CartStore";
import PaymentProcessingStore from "../../stores/PaymentProcessingStore";
import {
    getErrorMessage,
    priceFormatter,
    setupStyleMapFromStyleMap,
} from "../../utils/helpers";
import toast from "../../utils/toast";
import { useRouter } from "next/router";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { gtm } from "../../utils/gtm";
import SalesEndpoints from "../../api/SalesEndpoints";
import ApplicationStore from "../../stores/ApplicationStore";
import CartExpiryBanner from "../cart/CartExpiryBanner";
import InputField from "../form/InputField";
import CrossSellDialog from "../misc/CrossSellDialog";

const PaymentForm = dynamic(() => import("./PaymentForm"), { ssr: false });
const AddressForm = dynamic(() => import("./AddressForm"), { ssr: false });
const CustomFieldForm = dynamic(() => import("./CustomFieldForm"), {
    ssr: false,
});

type Props = {
    eventList: EcommerceEventList;
    clientConfig: ClientConfiguration | null;
    baseUrl?: string;
};

const CheckoutForm = observer(({ eventList, clientConfig, baseUrl }: Props) => {
    const [order, setOrder] = React.useState<Order | null>(null);
    const orderRef = React.useRef<Order | null>(order);
    const timerRef = React.useRef<NodeJS.Timeout | null>(null);
    const [address, setAddress] = React.useState<OrderAddress>({
        firstName: "",
        lastName: "",
        address: "",
        address2: "",
        city: "",
        stateId: null,
        countryId: null,
        postal: "",
        phone: "",
        email: "",
        emailConsent: false,
    });
    const [shippingAddress, setShippingAddress] = React.useState<OtherAddress>({
        firstName: "",
        lastName: "",
        address: "",
        address2: "",
        city: "",
        stateId: null,
        countryId: null,
        phone: "",
        postal: "",
        type: AddressType.SHIPPING,
    });
    const [useShippingAddress, setUseShippingAddress] = React.useState(false);
    const [ecommercePayment, setEcommercePayment] =
        React.useState<EcommercePayment>({
            discriminator: "ECOMMERCE",
            cardNum: "",
            expiryMonth: "",
            expiryYear: "",
            nameOnCard: "",
            cvv: "",
        });

    const [offlinePayment, setOfflinePayment] = React.useState<OfflinePayment>({
        discriminator: "OFFLINE",
        paymentMethod: PaymentMethod.CASH,
    });
    const [ticketFields, setTicketFields] = React.useState<{
        [ticketId: number]: { [fieldId: number]: any };
    }>({});
    const [customFields, setCustomFields] = React.useState<
        {
            fieldId: number;
            value:
                | string
                | number
                | boolean
                | {
                      optionId: number;
                      value: string | null;
                  }
                | {
                      optionId: number;
                      value: string | null;
                  }[]
                | null;
        }[]
    >([]);
    const [donations, setDonations] = React.useState<{ [key: number]: number }>(
        {},
    );
    const [isPaymentProcessing, setIsPaymentProcessing] = React.useState(false);

    const [step, setStep] = React.useState(0);

    const [showConfirmDialog, setShowConfirmDialog] = React.useState(true);

    const [wantsToCancel, setWantsToCancel] = React.useState(false);

    const { executeRecaptcha } = useGoogleReCaptcha();

    const addressRef = React.useRef<OrderAddress>(address);
    const ecommercePaymentRef =
        React.useRef<EcommercePayment>(ecommercePayment);
    const ticketFieldsRef = React.useRef<{
        [ticketId: number]: { [fieldId: number]: any };
    }>(ticketFields);

    addressRef.current = address;
    ecommercePaymentRef.current = ecommercePayment;
    ticketFieldsRef.current = ticketFields;

    const router = useRouter();

    function nextStep() {
        if (step === 0) {
            const cart = Object.assign({}, cartStore?.cart);
            gtm.trackBeginCheckout(cart);
        }
        if (step === (cartStore?.hasAnyCustomFields ? 4 : 3) - 2) {
            const cart = Object.assign({}, cartStore?.cart);
            gtm.trackAddShippingInfo(cart);
        }
        setStep(step + 1);
    }

    function previousStep() {
        setStep(Math.max(0, step - 1));
        setShowConfirmDialog(true);
    }

    function goToEventList() {
        router.push(`${baseUrl ?? `/store/${eventList.cartUrl}`}`);
    }

    function sayThanks(o: Order) {
        gtm.trackPurchase(o);
        router.push(`/store/${o.client.cartUrl}/thank-you/${o.retrievalKey}`);
    }

    async function onSquareSubmit(nonce: string, verificationToken?: string) {
        if (!executeRecaptcha) return;
        const token = await executeRecaptcha("checkout");
        doSquareCheckout({
            discriminator: "SQUARE_ECOMMERCE",
            squareNonce: nonce,
            verificationToken,
            reCaptchaResponse: token,
        });
    }

    async function onStripeSubmit(): Promise<string> {
        if (!executeRecaptcha) return "";
        const token = await executeRecaptcha("checkout");

        if (isPaymentProcessing) return "";

        setIsPaymentProcessing(true);

        try {
            const o = await completeOrder(
                addressRef.current,
                {
                    ...ecommercePaymentRef.current,
                    reCaptchaResponse: token,
                },
                useShippingAddress ? shippingAddress : undefined,
            );
            setOrder(o);
            orderRef.current = o;
            return o.paymentIntent?.paymentIntentToken ?? "";
        } catch (err) {
            console.log(err);
            return "";
        }
    }

    function onStripeSuccess() {
        cartStore?.removePromoCode();
        cartStore?.removeSalesTrackingKey();
        cartStore?.nextCart();
        setOrder((o) => {
            if (o) {
                sayThanks(o);
            }
            return o;
        });
    }

    function onStripeError(err: string) {
        setIsPaymentProcessing(false);
        toast.error(err);
        setOrder((o) => {
            if (o) {
                try {
                    SalesEndpoints.cancelOrder({
                        retrievalKey: o.retrievalKey,
                        cartKey: cartStore?.cartKey?.key ?? "",
                    });
                } catch (_) {}
            }
            return null;
        });
    }

    async function onIframeSubmit() {
        if (!cartStore || !cartStore.cartKey || isPaymentProcessing) {
            if (!cartStore) {
                console.log("No cart store");
            }
            if (!cartStore?.cartKey) {
                console.log("No cart key");
            }
            if (isPaymentProcessing) {
                console.log("Payment processing");
            }
            return;
        }
        let token: string | undefined = undefined;
        if (executeRecaptcha) {
            token = await executeRecaptcha("checkout");
        }

        if (AuthStore.loggedIn && AuthStore.boxoffice) {
            completeOrder(addressRef.current, offlinePayment)
                .then((o) => {
                    if (o.status === OrderStatus.PAYMENT_PENDING) {
                        setOrder(o);
                        orderRef.current = o;
                    } else if (o.status === OrderStatus.PURCHASED) {
                        sayThanks(o);
                    }
                })
                .catch((err) => {
                    // Already caught by completeOrder
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        } else if (cartStore.total === 0) {
            completeOrder(
                addressRef.current,
                {
                    ...ecommercePaymentRef.current,
                    reCaptchaResponse: token,
                },
                useShippingAddress ? shippingAddress : undefined,
            )
                .then((o) => {
                    sayThanks(o);
                })
                .catch((err) => {
                    // Already caught by completeOrder
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        } else {
            PaymentProcessingStore.processPayment(
                cartStore.cartKey.key,
                addressRef.current,
                {
                    ...ecommercePaymentRef.current,
                    reCaptchaResponse: token,
                },
            )
                .then((decoratedEcommercePayment) => {
                    completeOrder(
                        addressRef.current,
                        decoratedEcommercePayment,
                        useShippingAddress ? shippingAddress : undefined,
                    )
                        .then((o) => {
                            sayThanks(o);
                        })
                        .catch((err) => {
                            // Already caught by completeOrder
                        })
                        .finally(() => {
                            setIsPaymentProcessing(false);
                        });
                })
                .catch((err) => {
                    let msg =
                        "We were unable to connect to our credit card processor. Please refresh the page and try again.";
                    if (err) {
                        msg = err.messages || err.message || err.text || err;
                    }
                    toast.error(msg);
                    // StoreRepository.remoteLogging.error(
                    //     "Accept or Cardinal frontend processing failed for client key " + this.props.eventStore.clientKey + ": " + err,
                    //     this.props.cartStore.cartKey.key
                    // );
                    PaymentProcessingStore.updateCart(
                        cartStore.cartKey!.key,
                        cartStore.promoCode,
                    );
                })
                .finally(() => {
                    setIsPaymentProcessing(false);
                });
        }
    }

    const cartStore = useCartStore();

    function getOrderMetadata(): any {
        return {
            userAgent: navigator ? navigator.userAgent : "",
            referrer: document ? document.referrer : "",
            ...ApplicationStore.metadata,
        };
    }

    function doSquareCheckout(squarePayment: SquareOnlinePayment) {
        if (!squarePayment.squareNonce || isPaymentProcessing) return;

        setIsPaymentProcessing(true);

        completeOrder(
            addressRef.current,
            squarePayment,
            useShippingAddress ? shippingAddress : undefined,
        )
            .then((o) => {
                sayThanks(o);
            })
            .catch((err) => {
                // Already caught by completeOrder
            })
            .finally(() => {
                setIsPaymentProcessing(false);
            });
    }

    async function completeOrder(
        orderAddress: OrderAddress,
        payment: EcommercePayment | SquareOnlinePayment | OfflinePayment,
        shippingAddress?: OtherAddress,
    ): Promise<Order> {
        if (!cartStore) return Promise.reject("No cart store found");
        try {
            const o = await cartStore.completeOrder(
                orderAddress,
                payment,
                shippingAddress,
                getOrderMetadata(),
                ticketFieldsRef.current,
                customFields,
                donations,
            );

            if (o.status === OrderStatus.PAYMENT_PENDING) {
                return o;
            }

            const transactionLog = o.transactionLogs.reduce((a, b) =>
                new Date(a.transactionDate) > new Date(b.transactionDate)
                    ? a
                    : b,
            );
            if (transactionLog.status === TransactionLogStatus.SUCCESS) {
                cartStore.removePromoCode();
                cartStore.removeSalesTrackingKey();
                ApplicationStore.clearMetadata();
                cartStore.nextCart();
                return o;
            }
            if (transactionLog.status === TransactionLogStatus.DECLINED) {
                throw Error("Your payment was declined.");
            }
            throw Error(
                "An error occurred while processing transaction: " +
                    transactionLog.responseText,
            );
        } catch (err) {
            console.log(err);
            toast.error(getErrorMessage(err));
            throw err;
        }
    }

    function formIsValid(): boolean {
        if (
            !AuthStore.boxoffice &&
            address.firstName &&
            address.lastName &&
            address.address &&
            address.city &&
            address.stateId &&
            address.countryId &&
            address.postal &&
            address.phone &&
            address.email
        ) {
            if (isNaN(address.stateId) || isNaN(address.countryId)) {
                return false;
            }
            return true;
        }
        if (AuthStore.boxoffice) {
            if (address.firstName && address.lastName && address.email) {
                return true;
            }
        }
        return false;
    }

    React.useEffect(() => {
        if (eventList.newStyleMap) {
            setupStyleMapFromStyleMap(eventList.newStyleMap);
        }
        if (clientConfig) {
            PaymentProcessingStore.setClientConfig(clientConfig);
        }
    }, []);

    async function checkOrderStatus() {
        if (orderRef.current) {
            try {
                const res = await SalesEndpoints.getOrderByRetrievalKey(
                    orderRef.current.retrievalKey,
                );
                if (res.status === OrderStatus.PURCHASED) {
                    toast.success("Order completed successfully.");
                    sayThanks(res);
                    cartStore?.nextCart();
                    orderRef.current = null;
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                    }
                } else if (res.status === OrderStatus.PAYMENT_FAILED) {
                    toast.error("Payment failed. Please try again.");
                    orderRef.current = null;
                    setOrder(null);
                    setWantsToCancel(false);
                } else {
                    setOrder(res);
                    orderRef.current = res;
                }
            } catch (err) {
                setOrder(null);
                orderRef.current = null;
                setWantsToCancel(false);
                toast.error("Payment failed. Please try again.");
            }
        }
    }

    React.useEffect(() => {
        timerRef.current = setInterval(() => {
            checkOrderStatus();
        }, 5_000);
    }, []);

    const ticketSubtotal = cartStore?.ticketSubtotal ?? 0;
    const subtotal = cartStore?.subtotal ?? 0;
    const authFee = cartStore?.authFee ?? 0;
    const cartTotal = cartStore?.total ?? 0;
    const donationsTotal = Object.values(donations).reduce((a, b) => a + b, 0);
    const total = cartTotal + donationsTotal;

    const showSubtotal = subtotal !== cartTotal;
    const hasItems = (cartStore?.cart?.items.length || 0) > 0;
    const clientDonations = eventList.donations;
    const numSteps = (() => {
        if (cartStore?.hasAnyCustomFields && clientDonations.length) return 5;
        if (cartStore?.hasAnyCustomFields || clientDonations.length) return 4;
        return 3;
    })();
    const customFieldStep = cartStore?.hasAnyCustomFields ? 1 : -1;
    const donationStep = clientDonations.length
        ? cartStore?.hasAnyCustomFields
            ? 2
            : 1
        : -1;

    return (
        <PageWrapper eventList={eventList} baseUrl={baseUrl} noCart>
            <CrossSellDialog />
            <Container maxWidth="md">
                <Box sx={{ my: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={goToEventList}
                    >
                        Back to Event List
                    </Button>
                </Box>
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        {hasItems && cartStore?.shouldShowRefundMessage && (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="h5"
                                    style={{ color: "red" }}
                                    textAlign="center"
                                >
                                    NO REFUND OR EXCHANGE ON TICKETS
                                </Typography>
                            </Box>
                        )}
                        {cartStore?.newestItemDate && (
                            <Box sx={{ mb: 2 }}>
                                <CartExpiryBanner
                                    time={cartStore.newestItemDate}
                                    onTimeUp={() => {
                                        toast.error("Cart has expired.");
                                        cartStore.clearCart(false);
                                    }}
                                />
                            </Box>
                        )}
                        <Stepper
                            activeStep={step}
                            sx={{ display: { xs: "none", lg: "flex" } }}
                        >
                            <Step>
                                <StepLabel>Review Your Tickets</StepLabel>
                            </Step>
                            {cartStore?.hasAnyCustomFields && (
                                <Step>
                                    <StepLabel>Enter Ticket Info</StepLabel>
                                </Step>
                            )}
                            {clientDonations.length && (
                                <Step>
                                    <StepLabel>Donations</StepLabel>
                                </Step>
                            )}
                            <Step>
                                <StepLabel>Enter Your Address</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Pay for Your Tickets</StepLabel>
                            </Step>
                        </Stepper>
                        <MobileStepper
                            activeStep={step}
                            variant="dots"
                            steps={numSteps}
                            position="static"
                            backButton={<Box></Box>}
                            nextButton={<Box></Box>}
                            sx={{
                                width: "100%",
                                flexGrow: 1,
                                display: { xs: "flex", lg: "none" },
                            }}
                        />
                        {step === 0 && (
                            <>
                                {cartStore && (
                                    <CheckoutCart
                                        cartStore={cartStore}
                                        eventList={eventList}
                                        disablePromoCode={
                                            eventList.clientId === 112
                                        }
                                    />
                                )}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={nextStep}
                                    disabled={!cartStore?.hasItems}
                                >
                                    Next
                                </Button>
                            </>
                        )}
                        {step === customFieldStep && (
                            <form onSubmit={nextStep}>
                                <Grid container spacing={2}>
                                    {cartStore?.itemsWithCustomFields.map(
                                        (item, idx) => (
                                            <Grid
                                                item
                                                xs={12}
                                                key={item.ticketId}
                                            >
                                                <Card>
                                                    <CardContent>
                                                        <Box sx={{ my: 2 }}>
                                                            <Typography variant="h6">
                                                                {
                                                                    item
                                                                        .department
                                                                        .name
                                                                }{" "}
                                                                #{idx + 1}
                                                            </Typography>
                                                        </Box>
                                                        <Grid
                                                            container
                                                            spacing={2}
                                                        >
                                                            {item.department.customFields.map(
                                                                (field) => (
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        key={
                                                                            field.id
                                                                        }
                                                                    >
                                                                        <Typography
                                                                            variant="body1"
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                field.name
                                                                            }
                                                                        </Typography>
                                                                        {field.type ===
                                                                            "TEXT" && (
                                                                            <TextField
                                                                                fullWidth
                                                                                required={
                                                                                    field.required
                                                                                }
                                                                                variant="standard"
                                                                                value={
                                                                                    ticketFields[
                                                                                        item
                                                                                            .ticketId
                                                                                    ]
                                                                                        ? ticketFields[
                                                                                              item
                                                                                                  .ticketId
                                                                                          ][
                                                                                              field
                                                                                                  .id
                                                                                          ]
                                                                                        : ""
                                                                                }
                                                                                onChange={(
                                                                                    e,
                                                                                ) => {
                                                                                    setTicketFields(
                                                                                        {
                                                                                            ...ticketFields,
                                                                                            [item.ticketId]:
                                                                                                {
                                                                                                    ...ticketFields[
                                                                                                        item
                                                                                                            .ticketId
                                                                                                    ],
                                                                                                    [field.id]:
                                                                                                        e
                                                                                                            .target
                                                                                                            .value,
                                                                                                },
                                                                                        },
                                                                                    );
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {(field.type ===
                                                                            "SELECT_BOX" ||
                                                                            field.type ===
                                                                                "MULTI_SELECT_BOX") && (
                                                                            <FormControl
                                                                                fullWidth
                                                                                variant="standard"
                                                                                required={
                                                                                    field.required
                                                                                }
                                                                            >
                                                                                <Select
                                                                                    fullWidth
                                                                                    variant="standard"
                                                                                    label={
                                                                                        field.name
                                                                                    }
                                                                                    value={
                                                                                        ticketFields[
                                                                                            item
                                                                                                .ticketId
                                                                                        ]
                                                                                            ? ticketFields[
                                                                                                  item
                                                                                                      .ticketId
                                                                                              ][
                                                                                                  field
                                                                                                      .id
                                                                                              ] ??
                                                                                              []
                                                                                            : []
                                                                                    }
                                                                                    onChange={(
                                                                                        e,
                                                                                    ) => {
                                                                                        setTicketFields(
                                                                                            {
                                                                                                ...ticketFields,
                                                                                                [item.ticketId]:
                                                                                                    {
                                                                                                        ...ticketFields[
                                                                                                            item
                                                                                                                .ticketId
                                                                                                        ],
                                                                                                        [field.id]:
                                                                                                            field.type ===
                                                                                                            "SELECT_BOX"
                                                                                                                ? [
                                                                                                                      e
                                                                                                                          .target
                                                                                                                          .value,
                                                                                                                  ]
                                                                                                                : e
                                                                                                                      .target
                                                                                                                      .value,
                                                                                                    },
                                                                                            },
                                                                                        );
                                                                                    }}
                                                                                    multiple={
                                                                                        field.type ===
                                                                                        "MULTI_SELECT_BOX"
                                                                                    }
                                                                                >
                                                                                    {field.options.map(
                                                                                        (
                                                                                            option,
                                                                                        ) => (
                                                                                            <MenuItem
                                                                                                key={
                                                                                                    option.id
                                                                                                }
                                                                                                value={
                                                                                                    option.id
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    option.label
                                                                                                }
                                                                                            </MenuItem>
                                                                                        ),
                                                                                    )}
                                                                                </Select>
                                                                            </FormControl>
                                                                        )}
                                                                    </Grid>
                                                                ),
                                                            )}
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ),
                                    )}
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            type="button"
                                            fullWidth
                                            onClick={previousStep}
                                        >
                                            Back
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            fullWidth
                                            disabled={!cartStore?.hasItems}
                                        >
                                            Next
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                        {step === donationStep && (
                            <form onSubmit={nextStep}>
                                <Grid container spacing={2}>
                                    {clientDonations.map((donation) => (
                                        <Grid item xs={12} key={donation.id}>
                                            <Typography
                                                variant="h4"
                                                textAlign="center"
                                                sx={{ my: 2 }}
                                            >
                                                Donations
                                            </Typography>
                                            {/* {donation.allowCustomAmount && (
                                                <InputField
                                                    type="number"
                                                    label={donation.name}
                                                    value={
                                                        donations[
                                                            donation.id
                                                        ] ?? ""
                                                    }
                                                    onChange={(e) => {
                                                        setDonations(
                                                            (donations) => ({
                                                                ...donations,
                                                                [donation.id]:
                                                                    Number(
                                                                        e.target
                                                                            .value,
                                                                    ),
                                                            }),
                                                        );
                                                    }}
                                                />
                                            )} */}
                                            {!donation.allowCustomAmount && (
                                                <React.Fragment>
                                                    {donation.config.amounts
                                                        .length === 1 && (
                                                        <Card variant="outlined">
                                                            <CardContent>
                                                                <Typography
                                                                    variant="h6"
                                                                    sx={{
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    {donation
                                                                        .config
                                                                        .description ??
                                                                        `Would you like to make a donation to the ${donation.name}?`}
                                                                </Typography>
                                                                {donation.config
                                                                    .imageUrl && (
                                                                    <Box
                                                                        sx={{
                                                                            my: 2,
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={
                                                                                donation
                                                                                    .config
                                                                                    .imageUrl
                                                                            }
                                                                            alt={
                                                                                donation.name
                                                                            }
                                                                            style={{
                                                                                maxWidth:
                                                                                    "100%",
                                                                                width: "300px",
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                                <RadioGroup
                                                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                                                    name="controlled-radio-buttons-group"
                                                                    value={
                                                                        donations[
                                                                            donation
                                                                                .id
                                                                        ] ?? 0
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                        value,
                                                                    ) => {
                                                                        const num =
                                                                            parseInt(
                                                                                value,
                                                                                10,
                                                                            );
                                                                        if (
                                                                            num ===
                                                                            0
                                                                        ) {
                                                                            setDonations(
                                                                                (
                                                                                    donations,
                                                                                ) => {
                                                                                    delete donations[
                                                                                        donation
                                                                                            .id
                                                                                    ];
                                                                                    return {
                                                                                        ...donations,
                                                                                    };
                                                                                },
                                                                            );
                                                                        } else {
                                                                            setDonations(
                                                                                (
                                                                                    donations,
                                                                                ) => ({
                                                                                    ...donations,
                                                                                    [donation.id]:
                                                                                        parseInt(
                                                                                            value,
                                                                                            10,
                                                                                        ),
                                                                                }),
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        value={
                                                                            0
                                                                        }
                                                                        control={
                                                                            <Radio
                                                                                value={
                                                                                    0
                                                                                }
                                                                            />
                                                                        }
                                                                        label="No Thank You"
                                                                    />
                                                                    <FormControlLabel
                                                                        value={priceFormatter(
                                                                            donation
                                                                                .config
                                                                                .amounts[0],
                                                                        )}
                                                                        control={
                                                                            <Radio
                                                                                value={
                                                                                    donation
                                                                                        .config
                                                                                        .amounts[0]
                                                                                }
                                                                                checked={
                                                                                    donations[
                                                                                        donation
                                                                                            .id
                                                                                    ] ===
                                                                                    donation
                                                                                        .config
                                                                                        .amounts[0]
                                                                                }
                                                                            />
                                                                        }
                                                                        label={`Add ${priceFormatter(
                                                                            donation
                                                                                .config
                                                                                .amounts[0],
                                                                        )} to my order`}
                                                                    />
                                                                </RadioGroup>
                                                            </CardContent>
                                                        </Card>
                                                    )}
                                                </React.Fragment>
                                            )}
                                        </Grid>
                                    ))}
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            type="button"
                                            fullWidth
                                            onClick={previousStep}
                                        >
                                            Back
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            fullWidth
                                        >
                                            Next
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                        {step === numSteps - 2 && (
                            <Box sx={{ my: 2 }}>
                                <Typography variant="h4" textAlign="center">
                                    Address
                                </Typography>
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        if (!formIsValid()) {
                                            return toast.error(
                                                "Please fill out all required fields.",
                                            );
                                        }
                                        nextStep();
                                    }}
                                >
                                    <AddressForm
                                        termsAndConditions={
                                            eventList.termsAndConditions
                                        }
                                        clientName={eventList.clientName}
                                        clientId={eventList.clientId}
                                        address={address}
                                        setAddress={setAddress}
                                    />
                                    {eventList.customFields.length > 0 && (
                                        <Box sx={{ my: 2 }}>
                                            <Typography
                                                variant="h4"
                                                textAlign="center"
                                            >
                                                Additional Information
                                            </Typography>
                                            <CustomFieldForm
                                                customFieldDefinitions={
                                                    eventList.customFields
                                                }
                                                customFields={customFields}
                                                onChange={(fieldId, value) => {
                                                    setCustomFields(
                                                        (fields) => {
                                                            const idx =
                                                                fields.findIndex(
                                                                    (f) =>
                                                                        f.fieldId ===
                                                                        fieldId,
                                                                );
                                                            if (idx === -1) {
                                                                return [
                                                                    ...fields,
                                                                    {
                                                                        fieldId,
                                                                        value,
                                                                    },
                                                                ];
                                                            }
                                                            return fields.map(
                                                                (f, i) =>
                                                                    i === idx
                                                                        ? {
                                                                              fieldId,
                                                                              value,
                                                                          }
                                                                        : f,
                                                            );
                                                        },
                                                    );
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <Grid container spacing={2} sx={{ my: 1 }}>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                type="button"
                                                fullWidth
                                                onClick={previousStep}
                                            >
                                                Back
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                type="submit"
                                                fullWidth
                                                disabled={!cartStore?.hasItems}
                                            >
                                                Next
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Box>
                        )}
                        <Box
                            sx={{
                                my: step === numSteps - 1 ? 2 : 0,
                                visibility:
                                    step === numSteps - 1
                                        ? "visible"
                                        : "hidden",
                                height: step === numSteps - 1 ? "auto" : "0px",
                            }}
                        >
                            {cartStore && (
                                <>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{ my: 2, px: 2 }}
                                    >
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                Ticket Subtotal
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography
                                                variant="body2"
                                                textAlign="right"
                                            >
                                                {priceFormatter(ticketSubtotal)}
                                            </Typography>
                                        </Grid>
                                        {authFee !== 0 && (
                                            <>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">
                                                        Transaction Fee
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="body2"
                                                        textAlign="right"
                                                    >
                                                        {priceFormatter(
                                                            authFee,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                        {cartStore?.deliveryMethod && (
                                            <>
                                                <Grid item xs={8}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: "3px",
                                                        }}
                                                    >
                                                        {
                                                            cartStore
                                                                .deliveryMethod
                                                                .name
                                                        }
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="body2"
                                                        textAlign="right"
                                                    >
                                                        {priceFormatter(
                                                            cartStore
                                                                .deliveryMethod
                                                                .amount,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                        {showSubtotal && (
                                            <>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">
                                                        Subtotal
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="body2"
                                                        textAlign="right"
                                                    >
                                                        {priceFormatter(
                                                            subtotal,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                        {cartStore.taxes.map((it) => (
                                            <>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">
                                                        {it.name}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="body2"
                                                        textAlign="right"
                                                    >
                                                        {priceFormatter(
                                                            it.amount,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        ))}
                                        {donationsTotal > 0 && (
                                            <>
                                                <Grid item xs={8}>
                                                    <Typography variant="body2">
                                                        Donations
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <Typography
                                                        variant="body2"
                                                        textAlign="right"
                                                    >
                                                        {priceFormatter(
                                                            donationsTotal,
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </>
                                        )}
                                        <Grid item xs={8}>
                                            <Typography fontWeight={900}>
                                                Total
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} textAlign="right">
                                            <Typography>
                                                {priceFormatter(total)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    {(PaymentProcessingStore.clientConfiguration
                                        .gatewayId ||
                                        (!PaymentProcessingStore
                                            .clientConfiguration.gatewayId &&
                                            AuthStore.boxoffice)) && (
                                        <PaymentForm
                                            cartStore={cartStore}
                                            total={total + donationsTotal}
                                            order={order}
                                            address={address}
                                            ecommercePayment={ecommercePayment}
                                            setEcommercePayment={
                                                setEcommercePayment
                                            }
                                            offlinePayment={offlinePayment}
                                            setOfflinePayment={
                                                setOfflinePayment
                                            }
                                            onSquareSubmit={onSquareSubmit}
                                            onStripeSubmit={onStripeSubmit}
                                            onStripeSuccess={onStripeSuccess}
                                            onStripeError={onStripeError}
                                            onIframeSubmit={onIframeSubmit}
                                            onBack={previousStep}
                                            loading={
                                                cartStore.loading ||
                                                isPaymentProcessing
                                            }
                                        />
                                    )}
                                    {eventList?.clientId === 102 &&
                                        step === numSteps - 1 && (
                                            <Dialog
                                                open={showConfirmDialog}
                                                // onClose={() =>
                                                //     setShowConfirmDialog(false)
                                                // }
                                            >
                                                <DialogTitle>
                                                    Confirm Order
                                                </DialogTitle>
                                                <DialogContent>
                                                    <Typography>
                                                        By clicking
                                                        &quot;Confirm
                                                        Order&quot;, you agree
                                                        that the items listed
                                                        below are correct.
                                                    </Typography>
                                                    <Box sx={{ mt: 2 }}>
                                                        {cartStore.packageItems.map(
                                                            (item) => (
                                                                <Box
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        justifyContent:
                                                                            "space-between",
                                                                        alignItems:
                                                                            "center",
                                                                        mt: 1,
                                                                    }}
                                                                >
                                                                    <Typography>
                                                                        {
                                                                            item
                                                                                .eventPackage
                                                                                .name
                                                                        }{" "}
                                                                        |{" "}
                                                                        {
                                                                            item
                                                                                .eventPackagePriceGroup
                                                                                .name
                                                                        }{" "}
                                                                        -{" "}
                                                                        {
                                                                            item
                                                                                .seat
                                                                                ?.seatDetails
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            ),
                                                        )}
                                                        {cartStore.seatmapItems.map(
                                                            (item) => (
                                                                <Box
                                                                    key={
                                                                        item.ticketId
                                                                    }
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        justifyContent:
                                                                            "space-between",
                                                                        alignItems:
                                                                            "center",
                                                                        mt: 1,
                                                                    }}
                                                                >
                                                                    <Typography>
                                                                        {
                                                                            item
                                                                                .eventSeatmap
                                                                                ?.name
                                                                        }{" "}
                                                                        -{" "}
                                                                        {
                                                                            item
                                                                                .sectionSeat
                                                                                ?.seatDetails
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            ),
                                                        )}
                                                    </Box>
                                                </DialogContent>
                                                <DialogActions>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        onClick={() => {
                                                            previousStep();
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            setShowConfirmDialog(
                                                                false,
                                                            );
                                                        }}
                                                    >
                                                        Confirm Order
                                                    </Button>
                                                </DialogActions>
                                            </Dialog>
                                        )}
                                </>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Container>
            <Dialog open={!!order} fullWidth maxWidth="sm">
                {/* <DialogTitle>Complete Transaction on Terminal</DialogTitle> */}
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            minHeight: "200px",
                            justifyContent: "space-evenly",
                        }}
                    >
                        <Typography>
                            Your transaction has been sent to the Square
                            Terminal for processing.
                        </Typography>
                        <CircularProgress size={50} />
                        <Button
                            variant="contained"
                            color="error"
                            disabled={wantsToCancel}
                            onClick={() => {
                                setWantsToCancel(true);
                                SalesEndpoints.cancelSquareTerminalCheckout(
                                    order!.retrievalKey,
                                ).catch(() => {
                                    setWantsToCancel(false);
                                });
                            }}
                        >
                            Cancel Transaction
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </PageWrapper>
    );
});

export default CheckoutForm;
